// Section 22 person card: history and memory lines (narrated), family links, skill bars, a
// "why is this person unhappy" breakdown from explain(happinessTarget), and a rename control
// (Section 3; Decision.renames).
import { useState } from 'preact/hooks';
import type { PersonId } from '../../sim/api';
import type { PersonCardVM } from '../../viewModels';
import { oneDecimal, seasonName, template, whole } from '../format';
import { resourceLabel, uiText } from '../text/en';
import { Glyphs } from '../shell/glyphs';
import { narrationLine } from '../shell/text';
import { FactorList } from '../tooltip/factor-list';
import { Num } from '../tooltip/num';

interface Props {
  vm: PersonCardVM;
  canEdit: boolean;
  onRename: (name: string) => void;
  onOpenPerson: (id: PersonId) => void;
  onClose: () => void;
}

function memoryText(vm: PersonCardVM['memory'][number]): string {
  const resource = vm.resource === null ? 'rations' : resourceLabel[vm.resource].toLowerCase();
  return template(uiText.memory[vm.kind], { resource, other: vm.otherName ?? 'someone' });
}

function Rename({ vm, onRename }: Pick<Props, 'vm' | 'onRename'>) {
  const [value, setValue] = useState(vm.pendingName ?? vm.name);
  const t = uiText.person;
  return (
    <form
      class="person__rename"
      onSubmit={(e) => {
        e.preventDefault();
        onRename(value);
      }}
    >
      <label class="field field--inline">
        <span class="field__label">{t.renameLabel}</span>
        <input type="text" value={value} maxLength={40} onInput={(e) => setValue(e.currentTarget.value)} />
      </label>
      <button type="submit" class="button button--small">
        {t.renameSave}
      </button>
    </form>
  );
}

export function PersonCard({ vm, canEdit, onRename, onOpenPerson, onClose }: Props) {
  const t = uiText.person;
  return (
    <article class="person">
      <header class="overlay__head">
        <div>
          <h2 id="person-title" class="name">
            {vm.name}
          </h2>
          <p class="hint">
            {t.age(vm.ageYears)}, {uiText.sexLong[vm.sex]}, {uiText.origin[vm.origin].toLowerCase()} ·{' '}
            {t.job}: {vm.jobLabel}
            {vm.pendingJob && <span class="pending"> {uiText.people.pending}</span>}
          </p>
          {vm.pendingName !== null && <p class="pending">{t.renamePending(vm.pendingName)}</p>}
        </div>
        <button type="button" class="button button--small" onClick={onClose}>
          {t.close}
        </button>
      </header>
      <div class="person__grid">
        <section aria-labelledby="person-stats">
          <h3 id="person-stats">{t.stats}</h3>
          <dl class="person__stats">
            <dt>{t.health}</dt>
            <dd>
              <Num value={vm.health} format="whole" />
            </dd>
            <dt>{t.strength}</dt>
            <dd>
              <Num value={vm.strength} format="whole" />
            </dd>
            <dt>{t.happiness}</dt>
            <dd>
              <Num value={vm.happiness} format="whole" target={vm.targets.happiness} label={t.happiness} />
            </dd>
            <dt>{t.trust}</dt>
            <dd>
              <Num value={vm.trust} format="whole" />
            </dd>
          </dl>
          <p>
            <Glyphs flags={vm.flags} words />
          </p>
          <h3>{t.traits}</h3>
          <p>{vm.traits.length === 0 ? t.noTraits : vm.traits.map((x) => uiText.traits[x] ?? x).join(', ')}</p>
          {canEdit && <Rename vm={vm} onRename={onRename} />}
        </section>
        <section aria-labelledby="person-unhappy">
          <h3 id="person-unhappy">{t.whyUnhappy}</h3>
          <p class="hint">{t.happinessTarget(whole(vm.happinessTarget), whole(vm.happiness))}</p>
          {vm.unhappyBecause.length === 0 ? (
            <p>{t.notUnhappy}</p>
          ) : (
            <FactorList vm={{ value: vm.happinessTarget, groups: vm.happinessGroups }} format="whole" />
          )}
        </section>
        <section aria-labelledby="person-skills">
          <h3 id="person-skills">{t.skills}</h3>
          {vm.skills.length === 0 ? (
            <p class="hint">{t.noSkills}</p>
          ) : (
            <ul class="skillbars">
              {vm.skills.map((s) => (
                <li key={s.skill} class="skillbars__row">
                  <span class="skillbars__label">{s.label}</span>
                  <span class="bar" role="img" aria-label={`${oneDecimal(s.level)} of 5`}>
                    <span class="bar__fill" style={{ '--fill': `${(s.level / 5) * 100}%` }} />
                    {s.peak > s.level && (
                      <span class="bar__peak" style={{ '--peak': `${(s.peak / 5) * 100}%` }} />
                    )}
                  </span>
                  <span class="num">{oneDecimal(s.level)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-labelledby="person-family">
          <h3 id="person-family">{t.family}</h3>
          {vm.family.length === 0 ? (
            <p class="hint">{t.noFamily}</p>
          ) : (
            <ul class="person__family">
              {vm.family.map((f) => (
                <li key={f.id}>
                  {f.present ? (
                    <button type="button" class="link name" onClick={() => onOpenPerson(f.id)}>
                      {f.name}
                    </button>
                  ) : (
                    <span class="name">{f.name}</span>
                  )}{' '}
                  <span class="hint">
                    ({t.relation[f.relation]}
                    {f.fate !== null && `, ${t.fate[f.fate]}`})
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p class="hint">{uiText.list.household(vm.householdName, vm.householdSize)}</p>
        </section>
        <section aria-labelledby="person-memory">
          <h3 id="person-memory">{t.memory}</h3>
          {vm.memory.length === 0 ? (
            <p class="hint">{t.noMemory}</p>
          ) : (
            <ul class="prose-list">
              {vm.memory.map((m, i) => (
                <li key={i}>
                  <span class="hint">
                    {seasonName(m.season)} {m.year}:
                  </span>{' '}
                  {memoryText(m)}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-labelledby="person-history">
          <h3 id="person-history">{t.history}</h3>
          {vm.history.length === 0 ? (
            <p class="hint">{t.noHistory}</p>
          ) : (
            <ul class="prose-list">
              {vm.history.map((h, i) => (
                <li key={i}>
                  <span class="hint">
                    {seasonName(h.season)} {h.year}:
                  </span>{' '}
                  {narrationLine(h.narration)}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </article>
  );
}
